import { useEffect } from 'react';
import { updateAppointmentStatus } from '../services/appointmentService';

/**
 * Hook: useAutoExpiration
 * Verifica e expira automaticamente solicitações pendentes
 * 
 * NOTA: A limpeza de slots expirados é feita EXCLUSIVAMENTE pelo hook useSlotCleanup
 * (em AppContext) para evitar duplicação e conflitos.
 * 
 * @param {Array} appointments - Lista de agendamentos
 * @param {Array} slots - Lista de slots disponíveis (mantido para compatibilidade, mas não usado para deletar)
 * @param {number} checkInterval - Intervalo de verificação em ms (padrão: 5 minutos)
 */
export const useAutoExpiration = (appointments = [], slots = [], checkInterval = 12 * 60 * 60 * 1000) => {
  
  useEffect(() => {
    const checkExpiredItems = async () => {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const currentDate = `${year}-${month}-${day}`; // YYYY-MM-DD (local)
      const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`; // HH:MM (local)
      
      console.log('🕐 Verificando agendamentos expirados...', { currentDate, currentTime });
      
      // Verificar solicitações pendentes expiradas
      const expiredAppointments = appointments.filter(apt => {
        if (apt.status !== 'pending') return false;
        
        // Se a data é passada
        if (apt.date < currentDate) return true;
        
        // Se é hoje mas o horário já passou
        if (apt.date === currentDate && apt.time < currentTime) return true;
        
        return false;
      });
      
      // Atualizar status para 'cancelled'
      for (const apt of expiredAppointments) {
        console.log(`⏰ Expirando agendamento: ${apt.id} (${apt.date} ${apt.time})`);
        await updateAppointmentStatus(apt.id, 'cancelled', { 
          cancelReason: 'Expirado automaticamente',
          expiredAt: now.toISOString()
        });
      }
      
      // NOTA: Slots expirados são gerenciados pelo useSlotCleanup (AppContext)
      // Não deletar slots aqui para evitar conflitos entre os dois hooks
      
      if (expiredAppointments.length > 0) {
        console.log(`✅ Auto-expiração: ${expiredAppointments.length} agendamento(s) expirado(s)`);
      }
    };
    
    // Executar imediatamente
    checkExpiredItems();
    
    // Executar a cada intervalo
    const intervalId = setInterval(checkExpiredItems, checkInterval);
    
    // Cleanup
    return () => clearInterval(intervalId);
  }, [appointments, checkInterval]);
};

/**
 * Função auxiliar para verificar se um item está expirado
 * Usa Date objects para comparação segura
 */
export const isExpired = (date, time) => {
  const now = new Date();
  
  // Criar Date object para o horário do slot
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);
  const itemDateTime = new Date(year, month - 1, day, hours, minutes, 0);
  
  return now > itemDateTime;
};
